import {
  TEXT_PHOTO_QUALITY,
  outboundMediaPath,
  outboundMediaType,
  shrunkPhotoProblem,
  shrunkSize,
  unsendablePhotoProblem,
} from "@roof-hawk/core";

import { supabase } from "./supabase";

/**
 * Getting a picture ready to text (ADR 0310).
 *
 * A photograph off a phone is five to twelve megabytes and, on an iPhone,
 * HEIC, which no carrier delivers. So before it goes anywhere it is drawn
 * onto a canvas no wider than `TEXT_PHOTO_MAX_EDGE` and written back out as
 * a JPEG — the browser decodes HEIC where the phone can (Safari on iOS), and
 * the result is a picture a text message carries. Then it is put under
 * `outbound/` in the private `message-media` bucket, which the storage
 * policy lets a signed-in rep write to, and the path is what `sendText`
 * sends up.
 *
 * Where the browser cannot decode it (Chrome on a desktop, with a HEIC off
 * an iPhone), the file is as it was chosen. A HEIC is then refused here, in
 * words, before anything is uploaded: `twilio-send` would refuse it anyway,
 * and an upload the server then turns away is a picture that seems to go and
 * never does.
 */

/** The picture as a JPEG no bigger than a text can carry, and whether it
 * was: `shrunk` is false when this browser could not draw it, and `blob` is
 * then the file as chosen. */
export async function shrinkPhoto(
  file: File,
): Promise<{ blob: Blob; shrunk: boolean }> {
  const asChosen = { blob: file as Blob, shrunk: false };
  const bitmap = await decode(file);
  if (!bitmap) return asChosen;
  try {
    const { width, height } = shrunkSize(bitmap.width, bitmap.height);
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) return asChosen;
    context.drawImage(bitmap, 0, 0, width, height);
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", TEXT_PHOTO_QUALITY),
    );
    return blob ? { blob, shrunk: true } : asChosen;
  } finally {
    if ("close" in bitmap) bitmap.close();
  }
}

/** The picture decoded, or null where this browser cannot. */
async function decode(file: File): Promise<ImageBitmap | null> {
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(file);
    } catch {
      // Fall through: Chrome cannot read HEIC, and an <img> cannot either.
      // `uploadTextPhoto` decides whether the file as chosen may still go.
    }
  }
  return null;
}

/** The extension a stored picture is given, from what it actually is. */
const EXTENSIONS: Record<string, string> = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/gif": "gif",
  "image/webp": "webp",
};

/**
 * Shrink the picture and put it in the bucket. Returns the stored path, or
 * throws a sentence the screen can show, having uploaded nothing.
 */
export async function uploadTextPhoto(file: File): Promise<string> {
  const { blob, shrunk } = await shrinkPhoto(file);
  const type = blob.type.toLowerCase();
  const unsendable = unsendablePhotoProblem(type);
  if (unsendable) throw new Error(unsendable);
  const problem = shrunkPhotoProblem(blob.size, shrunk);
  if (problem) throw new Error(problem);

  const path = outboundMediaPath(
    crypto.randomUUID(),
    EXTENSIONS[type] ?? "jpg",
  );
  const { error } = await supabase.storage
    .from("message-media")
    .upload(path, blob, {
      contentType: type || outboundMediaType(path),
      upsert: false,
    });
  if (error)
    throw new Error(`The picture could not be uploaded: ${error.message}`);
  return path;
}
